import { isSet } from "../../../../../models/model-types-methods";
import { TRISTATE_CHECKBOX_VALUES } from "../../../tristate-checkbox/tristate-checkbox";
import { ICheckboxesTreeItem } from "./checkboxes-tree-store";

export class CheckboxesTreeManager {
    private _options: ICheckboxesTreeItem[] = [];
    private _value: ICheckboxesTreeItem[]|undefined;
    private _enrichedOptions: ICheckboxesTreeItem[] = [];
    private _isCompressed: boolean = false;

    constructor(options: ICheckboxesTreeItem[], isCompressed: boolean = false) {
        this._options = options;
        this._isCompressed = isCompressed;
        this._enrichedOptions = this._enrichOptionWithCheckBoxValue(this._options);
    }

    public setValue(value: ICheckboxesTreeItem[]|undefined) {
        this._value = value;
        this._enrichedOptions = this._enrichOptionWithCheckBoxValue(this._options);
    }

    /** should return a constructed ICheckboxesTreeItem */
    public getOptions(): ICheckboxesTreeItem[] {
        return this._enrichedOptions;
    }

    /** returns a new array of selected values without checkBoxValue property */
    public extractSelectedValues(updatedEnrichedValues: ICheckboxesTreeItem[]): ICheckboxesTreeItem[]|undefined {
       const [selectedOptions, areAllChildNodesChecked] = this._extractSelectedValues(updatedEnrichedValues, [], []);

        /** When nothing is selected (explicit Unselect All), return undefined */
        if (!areAllChildNodesChecked && selectedOptions.length === 0) {
            return undefined;
        }

        return selectedOptions;        
    }

    private _extractSelectedValues(updatedEnrichedValues: ICheckboxesTreeItem[], path: string[], selectedOptions: ICheckboxesTreeItem[]): [ICheckboxesTreeItem[], boolean] {
        
        let uncheckedNodes = updatedEnrichedValues.length;

        updatedEnrichedValues.forEach((currentOption) => {

            /** copy option item without nested items */
            const { childNodes, checkBoxValue, ...copiedOption } = currentOption;
            path.push(currentOption.value);

            if (!isSet(childNodes) || childNodes.length === 0) {
                /** we reached a leaf, if checked - take this item */
                if (currentOption.checkBoxValue === TRISTATE_CHECKBOX_VALUES.CHECKED) {
                    /** collect this item into selected */
                    this._mergeIntoSelected(selectedOptions, path);
                    uncheckedNodes -= 1;
                }
                path.pop();
                return;
            }

            /** traversing deeper into child nodes */
            const [_childNodes, areAllChildNodesChecked] = this._extractSelectedValues(childNodes, path, selectedOptions);
            if (areAllChildNodesChecked) {
                uncheckedNodes -= 1;
            }

            copiedOption.childNodes = _childNodes;
            path.pop();
        }); 
    

        if (this._isCompressed) {

            /** when all nodes at this level are fully selected (uncheckedNodes === 0), remove the child nodes when in compressed mode */
            if (uncheckedNodes === 0) {
                /** we are at Root level, remove all child nodes */
                if (path.length === 0) {
                    /** in compressed mode empty array is treated as if all nodes are fully selected */
                    selectedOptions = [];
                }
                /** we are not at Root level - find and remove child nodes by the path from selectedOptions  */
                else {
                    let currentItemList = selectedOptions;
                    let currentItem: ICheckboxesTreeItem|undefined = undefined;
                    path.forEach(pathItem => {
                        currentItem = currentItemList.find((option) => option.value === pathItem);
                        currentItemList = currentItem?.childNodes ?? [];
                    });

                    if (!isSet(currentItem)) {
                        console.error(`Should not happen, Could not find item in selected tree by path`, path);
                    } 
                    else {
                        /** remove all nested items, making it a compressed node */
                        delete (currentItem as ICheckboxesTreeItem).childNodes;
                    }
                }
            }
        }

        return [selectedOptions, uncheckedNodes === 0];
    }


    /** Merges one value path into selected options, descending via SELECTED nodes. */
    private _mergeIntoSelected(selectedOptions: ICheckboxesTreeItem[], path: string[]): void {
        let currentSelectedList: ICheckboxesTreeItem[] = selectedOptions;
        let currentOptionList: ICheckboxesTreeItem[]|undefined = this._options;

        path.forEach((pathItem, index) => {

            /** find the corresponding option node in the original options tree */
            const currentItem = currentOptionList?.find((option) => option.value === pathItem);
            currentOptionList = currentItem?.childNodes; /** moving the pointer to next level */

            if (!isSet(currentItem)) {
                /** if item was not found, which shouldn't happen, do nothing */
                console.error(`Should not happen, Could not find item in options tree byt path`, path);
                return;
            }

            /** find or create the node inside the selected tree at this level */
            let selectedItem = currentSelectedList.find((option) => option.value === pathItem);
            if (!isSet(selectedItem)) {
                /** selectedNode is currentIem without the childNodes and checkBoxValue */
                const { childNodes, checkBoxValue, ...selectedNode } = currentItem;
                selectedItem = { ...selectedNode };
                currentSelectedList.push(selectedItem);
            }

            if (isSet(currentOptionList) && currentOptionList.length > 0) {
                selectedItem.childNodes = selectedItem.childNodes ?? [];
                currentSelectedList = selectedItem.childNodes; /** moving the pointer to next level */
            }
        });
    }

    /** works only for leafs */
    private _isSelected(path: string[]): boolean {
        /** when value is underfined - nothing is selected */
        if (!isSet(this._value)) {
            return false;
        }

        if (this._isCompressed && this._value.length === 0) {
            /** when values is empty and compression is on - it means all items are selected */
            return true;
        }

        let currentItemList: ICheckboxesTreeItem[]|undefined = this._value;
        let currentItem: ICheckboxesTreeItem|undefined = undefined;
        let parentNode: ICheckboxesTreeItem|undefined = undefined;

        path.forEach((pathItem, index) => {
            currentItem = currentItemList?.find((option) => option.value === pathItem);
            currentItemList = currentItem?.childNodes;

            if (index + 1 < path.length && isSet(currentItem)) {
                parentNode = currentItem;
            }
        });

        if (this._isCompressed) {
            /** when values have empty parent node on any level - treat it as all inner nodes are selected */
            if (isSet(parentNode) && ((parentNode as ICheckboxesTreeItem).childNodes?.length ?? 0) === 0) {
                return true;
            }   
        }

        return isSet(currentItem);
    }

    /** Makes deep copy of passed options and adds property checkBoxValue */
    private _enrichOptionWithCheckBoxValue(options: ICheckboxesTreeItem[], path: string[] = []): ICheckboxesTreeItem[] {
        const copiedOptions: ICheckboxesTreeItem[] = [];
        
        options.forEach((currentOption) => {

            /** copy option item without nested items */
            const { childNodes, ...copiedOption } = currentOption;
            copiedOptions.push(copiedOption);
            path.push(currentOption.value);

            /** if True, it means we reached leaf, the lowest level item without child items  */
            if ((childNodes?.length ?? 0) === 0) {
                copiedOption.checkBoxValue = this._isSelected(path) ? TRISTATE_CHECKBOX_VALUES.CHECKED : TRISTATE_CHECKBOX_VALUES.UNCHECKED;
                path.pop();
                return;
            }

            copiedOption.childNodes = this._enrichOptionWithCheckBoxValue(childNodes!, path);
            path.pop();
        });

        return copiedOptions;
    }

}

export default CheckboxesTreeManager;